import React, { useState, useMemo, useEffect, useRef } from 'react';
import { _T, C } from '../../shared/theme';
import { playSound } from '../../shared/soundEngine';
import { s_PB, s_GB, s_GC, s_TI, s_PL } from '../../shared/styles';
import { calcLoad } from '../../shared/utils';
import RingGauge from '../../components/ui/RingGauge';
import { EXERCISES } from '../exercises/constants';
import WeeklyIntention from '../onboarding/WeeklyIntention';

const BASE = process.env.REACT_APP_API_URL || '/api';

function toDateStr(d) {
  return d.getFullYear() + '-'
    + String(d.getMonth() + 1).padStart(2, '0') + '-'
    + String(d.getDate()).padStart(2, '0');
}

// ── Share channels ─────────────────────────────────────────────────────────────
const CHANNELS = [
  { id: 'whatsapp', icon: '💬', label: 'WhatsApp', color: '#25D366' },
  { id: 'signal',   icon: '🔵', label: 'Signal',   color: '#3A76F0' },
  { id: 'viber',    icon: '🟣', label: 'Viber',    color: '#7360F2' },
  { id: 'facebook', icon: '📘', label: 'Facebook', color: '#1877F2' },
  { id: 'linkedin', icon: '🔗', label: 'LinkedIn', color: '#0A66C2' },
  { id: 'email',    icon: '📧', label: 'Email',    color: '#C8A84B' },
];

async function createInviteToken({ title, date, dayLabel, start, end, description, creatorId, creatorName }) {
  try {
    const res = await fetch(`${BASE}/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, date, day: dayLabel, start_time: start, end_time: end, description, creator_id: creatorId, creator_name: creatorName }),
    });
    const d = await res.json();
    return d.token || null;
  } catch { return null; }
}

function buildInviteText({ title, dateLabel, start, end, token, lang }) {
  const nl = lang === 'nl';
  const link = `${window.location.origin}/?apptinvite=${token}`;
  return nl
    ? `Hoi! Ik nodig je uit voor *${title}* op ${dateLabel} van ${start} tot ${end}.\n\nAccepteer of weiger hier: ${link}`
    : `Hi! I'd like to invite you to *${title}* on ${dateLabel} from ${start} to ${end}.\n\nAccept or decline here: ${link}`;
}

// ── Invite modal ───────────────────────────────────────────────────────────────
function InviteModal({ item, dateLabel, dateKey, lang, creatorId, creatorName, onClose, onAttendeeAdded }) {
  const nl = lang === 'nl';
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [copied, setCopied] = useState(false);
  const [tokenCache, setTokenCache] = useState(null);

  const getToken = async () => {
    if (tokenCache) return tokenCache;
    const t = await createInviteToken({
      title: item.label,
      date: dateKey,
      dayLabel: dateLabel,
      start: item.startTime || '',
      end: item.endTime || '',
      description: item.description || '',
      creatorId,
      creatorName,
    });
    if (t) setTokenCache(t);
    return t;
  };

  const shareChannel = async (ch) => {
    const token = await getToken();
    if (!token) return;
    const link = `${window.location.origin}/?apptinvite=${token}`;
    const text = buildInviteText({ title: item.label, dateLabel, start: item.startTime || '', end: item.endTime || '', token, lang });

    if (ch.id === 'email') return; // handled by email form
    if (ch.id === 'signal') {
      try { await navigator.clipboard.writeText(link); } catch {}
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      onAttendeeAdded(token, ch.id);
      return;
    }
    let url;
    if (ch.id === 'whatsapp') url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    else if (ch.id === 'viber') url = `viber://forward?text=${encodeURIComponent(text)}`;
    else if (ch.id === 'facebook') url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`;
    else if (ch.id === 'linkedin') url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`;
    window.open(url, '_blank');
    onAttendeeAdded(token, ch.id);
  };

  const sendEmail = async () => {
    if (!email.includes('@')) return;
    setEmailStatus('sending');
    const token = await getToken();
    const link = token ? `${window.location.origin}/?apptinvite=${token}` : '';
    try {
      const res = await fetch(`${BASE}/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, title: item.label, date: dateKey, day: dateLabel, start: item.startTime || '', end: item.endTime || '', description: item.description || '', creator_id: creatorId, creator_name: creatorName }),
      });
      const d = await res.json();
      setEmailStatus(d.ok ? 'sent' : 'error');
      if (d.token) onAttendeeAdded(d.token, 'email');
      else if (token) onAttendeeAdded(token, 'email');
      if (link) {
        // Also open email client as fallback
        const subject = nl ? `Uitnodiging: ${item.label}` : `Invitation: ${item.label}`;
        const body = nl
          ? `Hoi,\n\nIk nodig je uit voor ${item.label} op ${dateLabel} van ${item.startTime||''} tot ${item.endTime||''}.\n\nAccepteer of weiger hier: ${link}\n\nGroeten`
          : `Hi,\n\nI'd like to invite you to ${item.label} on ${dateLabel} from ${item.startTime||''} to ${item.endTime||''}.\n\nAccept or decline here: ${link}\n\nBest`;
        setTimeout(() => window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, 300);
      }
    } catch { setEmailStatus('error'); }
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:'fixed',inset:0,zIndex:999,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'flex-end' }}>
      <div style={{ width:'100%',background:_T.bg2,borderRadius:'20px 20px 0 0',padding:'22px 18px 32px',maxHeight:'90vh',overflowY:'auto' }}>
        <div style={{ fontSize:12,color:C.orange,fontWeight:700,marginBottom:4 }}>📩 {nl?'Uitnodigen voor':'Invite to'}</div>
        <div style={{ color:C.white,fontSize:15,fontWeight:700,marginBottom:2 }}>{item.label}</div>
        <div style={{ color:C.silver,fontSize:12,marginBottom:18 }}>{dateLabel} · {item.startTime||''}{item.endTime?` – ${item.endTime}`:''}</div>

        {/* Channel buttons */}
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:16 }}>
          {CHANNELS.filter(ch => ch.id !== 'email').map(ch => (
            <button key={ch.id} onClick={() => shareChannel(ch)}
              style={{ background:`${ch.color}18`,border:`1px solid ${ch.color}44`,borderRadius:12,padding:'10px 6px',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:4 }}>
              <span style={{ fontSize:18 }}>{ch.icon}</span>
              <span style={{ fontSize:10,color:ch.color,fontWeight:600 }}>
                {ch.id === 'signal' ? (copied ? (nl?'✓ Gekopieerd':'✓ Copied') : ch.label) : ch.label}
              </span>
            </button>
          ))}
        </div>

        {/* Email form */}
        <div style={{ borderTop:`1px solid ${_T.cardBorderBot}`,paddingTop:14,marginBottom:12 }}>
          <div style={{ fontSize:11,color:C.silver,fontWeight:600,marginBottom:8 }}>📧 {nl?'Via e-mail':'Via email'}</div>
          <div style={{ display:'flex',gap:8 }}>
            <input value={email} onChange={e=>{ setEmail(e.target.value); setEmailStatus(null); }}
              placeholder={nl?'E-mailadres...':'Email address...'} type="email"
              style={{ flex:1,background:_T.bg3,border:`1px solid ${_T.cardBorderBot}`,color:C.white,borderRadius:10,padding:'9px 12px',fontSize:13 }}/>
            <button onClick={sendEmail} disabled={!email.includes('@')||emailStatus==='sending'}
              style={{ background:emailStatus==='sent'?`${C.teal}20`:C.orange,border:`1px solid ${emailStatus==='sent'?C.teal:C.orange}`,color:emailStatus==='sent'?C.teal:'#0a1220',borderRadius:10,padding:'9px 14px',cursor:'pointer',fontSize:12,fontWeight:700,opacity:email.includes('@')?1:0.5 }}>
              {emailStatus==='sending'?'···':emailStatus==='sent'?(nl?'✓ Verzonden':'✓ Sent'):(nl?'Stuur':'Send')}
            </button>
          </div>
          {emailStatus==='error'&&<div style={{ fontSize:11,color:C.red,marginTop:4 }}>{nl?'Verzenden mislukt.':'Send failed.'}</div>}
        </div>

        <button onClick={onClose} style={{ width:'100%',background:'transparent',border:`1px solid ${_T.cardBorderBot}`,color:C.silver,borderRadius:10,padding:'11px',cursor:'pointer',fontSize:13 }}>
          {nl?'Sluiten':'Close'}
        </button>
      </div>
    </div>
  );
}

// ── Intention Coach ────────────────────────────────────────────────────────────
function bold(text) {
  return text.split('**').map((part, i) =>
    i % 2 === 1 ? <strong key={i} style={{ color:'#EEF3FF' }}>{part}</strong> : part
  );
}

function getWeekKey() {
  const d = new Date();
  const dow = d.getDay() || 7;
  d.setDate(d.getDate() - (dow - 1));
  return `sl_coach_dismissed_${d.toISOString().slice(0, 10)}`;
}

function IntentionCoach({ intention, setIntention, weekAgenda, datedAgenda, lang }) {
  const nl = lang === 'nl';
  const [shown, setShown] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(getWeekKey()) || '{}'); } catch { return {}; }
  });
  const touchStartY = useRef({});

  useEffect(() => {
    const t = setTimeout(() => setShown(true), 100);
    return () => clearTimeout(t);
  }, []);

  const sleepLog  = useMemo(() => { try { return JSON.parse(localStorage.getItem('sl_sleep_log') || '[]'); } catch { return []; } }, []);
  const moodLog   = useMemo(() => { try { return JSON.parse(localStorage.getItem('sl_mood_log')   || '[]'); } catch { return []; } }, []);
  const exHistory = useMemo(() => { try { return JSON.parse(localStorage.getItem('sl_ex_history') || '[]'); } catch { return []; } }, []);

  // All hooks declared before any conditional return ─────────────────────────
  const messages = useMemo(() => {
    if (!intention) return [];
    const itl = intention.toLowerCase();
    const has = (...words) => words.some(w => itl.includes(w));

    // Intention type detection
    const isStress   = has('stress','rustig','calm','ontspan','relax','angst','anxiety','spanning');
    const isFocus    = has('focus','concentr','productief','productiv','werk','work','studie','study','scherpte');
    const isEnergy   = has('energie','energy','vitaal','vital','wakker','actief','active','refresh');
    const isStrength = has('sterk','strong','kracht','fit','sport','power','spier','muscle','train');
    const isSleep    = has('slaap','sleep','rust','rest','moe','tired','vermoeid');
    const isMood     = has('humeur','mood','blij','happy','positief','positive','gevoel','stemming');

    // Data analysis
    const todayKey = new Date().toISOString().slice(0, 10);
    const last7 = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - i); return d.toISOString().slice(0, 10);
    });
    const recentSleep = last7.map(d => sleepLog.find(s => s.date === d)).filter(Boolean);
    const avgSleep = recentSleep.length
      ? recentSleep.reduce((a, s) => a + (s.score ?? 5), 0) / recentSleep.length
      : null;
    const todayMood = moodLog.find(m => m.date === todayKey);
    const MOOD_SCORE = { great: 5, good: 4, neutral: 3, low: 2, bad: 1 };
    const moodScore = todayMood ? (todayMood.score ?? MOOD_SCORE[todayMood.mood] ?? null) : null;

    const weekStartKey = last7[6];
    const recentEx = (exHistory || []).filter(e => (e.date || '') >= weekStartKey);
    const exDone = new Set(recentEx.map(e => e.exId || e.id));

    const allWeekItems = Object.values(weekAgenda || {}).flat().concat(Object.values(datedAgenda || {}).flat());
    const totalDur = allWeekItems.reduce((a, i) => a + (i.duration || 0), 0);
    const mentalLoad = totalDur ? Math.min(100, allWeekItems.reduce((a, i) => a + (i.mental || 0) * (i.duration || 0), 0) / totalDur) : 0;
    const physicalLoad = totalDur ? Math.min(100, allWeekItems.reduce((a, i) => a + (i.physical || 0) * (i.duration || 0), 0) / totalDur) : 0;
    const highMental   = mentalLoad > 60;
    const highPhysical = physicalLoad > 60;
    const lowSleep     = avgSleep !== null && avgSleep < 6;
    const lowMood      = moodScore !== null && moodScore <= 2;

    const msgs = [];

    // ── 1. Opening ────────────────────────────────────────────────────────────
    const openings = nl ? {
      stress:   { icon:'🌊', msg:`Je intentie om kalmte te vinden is krachtig. Ik begeleid je deze week naar echte ontspanning — stap voor stap.` },
      focus:    { icon:'🎯', msg:`Focus bereik je niet door harder te werken, maar slimmer. Ik help je de juiste oefeningen en momenten kiezen.` },
      energy:   { icon:'⚡', msg:`Meer energie begint bij bewuste keuzes. We richten ons op activatie, herstel en het juiste ritme.` },
      strength: { icon:'💪', msg:`Kracht bouwen gaat verder dan fysiek trainen — het is ook mentale weerbaarheid. Ik combineer beide voor je.` },
      sleep:    { icon:'🌙', msg:`Betere slaap begint overdag. Ik help je ontspanningsoefeningen en slimme gewoontes inplannen.` },
      mood:     { icon:'☀️', msg:`Je stemming verbeteren is een vaardigheid. De juiste kleine acties zorgen voor een groot verschil.` },
      default:  { icon:'✦',  msg:`Je hebt een richting gekozen. Nu vullen we die in met de juiste oefeningen, spellen en rustmomenten.` },
    } : {
      stress:   { icon:'🌊', msg:`Your intention to find calm is powerful. I'll guide you toward real relaxation this week — step by step.` },
      focus:    { icon:'🎯', msg:`Focus isn't achieved by working harder, but smarter. I'll help you choose the right exercises and moments.` },
      energy:   { icon:'⚡', msg:`More energy starts with conscious choices. We'll focus on activation, recovery and the right rhythm.` },
      strength: { icon:'💪', msg:`Building strength goes beyond physical training — it's mental resilience too. I'll combine both for you.` },
      sleep:    { icon:'🌙', msg:`Better sleep starts during the day. I'll help you plan relaxation exercises and habits that actually work.` },
      mood:     { icon:'☀️', msg:`Improving your mood is a skill. The right small actions create a big difference over time.` },
      default:  { icon:'✦',  msg:`You've chosen a direction. Now let's fill it with the right exercises, games and recovery moments.` },
    };
    const opening = isStress ? openings.stress : isFocus ? openings.focus : isEnergy ? openings.energy
      : isStrength ? openings.strength : isSleep ? openings.sleep : isMood ? openings.mood : openings.default;
    msgs.push({ ...opening, cat: nl ? 'JOUW WEEK' : 'YOUR WEEK', basis: nl ? 'Op basis van: jouw intentie' : 'Based on: your intention' });

    // ── 2. Exercise recommendation ────────────────────────────────────────────
    const exOptions = {
      breathing:  { icon:'🌬️', en:'Box Breathing (5 min)',       nl:'Box Breathing (5 min)',       why_en:'activates your parasympathetic nervous system and lowers cortisol fast',    why_nl:'activeert je parasympatisch zenuwstelsel en verlaagt cortisol snel' },
      focus:      { icon:'🎯', en:'Focus Session (10 min)',       nl:'Focussessie (10 min)',        why_en:'primes your prefrontal cortex for deep, distraction-free work',             why_nl:'bereidt je prefrontale cortex voor op diepe, onafleide focus' },
      wimhof:     { icon:'❄️', en:'Wim Hof Breathing (7 min)',   nl:'Wim Hof Ademhaling (7 min)', why_en:'rapidly raises oxygen, adrenaline and full-body alertness',                 why_nl:'verhoogt snel zuurstof, adrenaline en alertheid' },
      powerpose:  { icon:'🦁', en:'Power Pose (5 min)',           nl:'Power Pose (5 min)',          why_en:'boosts testosterone and confidence hormones before a challenge',            why_nl:'verhoogt testosteron en zelfvertrouwen voor een uitdaging' },
      meditation: { icon:'🧘', en:'Meditation (10 min)',          nl:'Meditatie (10 min)',          why_en:'signals your brain to slow down and prepares the body for deeper sleep',    why_nl:'geeft je brein het signaal te vertragen en bereidt het lichaam voor op slaap' },
      bodyscan:   { icon:'🔍', en:'Body Scan (8 min)',            nl:'Lichaamsscan (8 min)',        why_en:'releases mental tension stored physically in the body',                     why_nl:'lost mentale spanning op die fysiek in het lichaam is opgeslagen' },
      gratitude:  { icon:'🙏', en:'Gratitude Practice (5 min)',   nl:'Dankbaarheidsoefening (5 min)', why_en:'rewires neural pathways toward positive pattern recognition',            why_nl:'herprogrammeert neurale paden richting positieve patroonherkenning' },
      yoga:       { icon:'🌿', en:'Yoga Flow (15 min)',           nl:'Yoga Flow (15 min)',          why_en:'combines physical release with breath control to reset your system',        why_nl:'combineert fysieke ontspanning met ademcontrole om je systeem te resetten' },
      stretching: { icon:'🤸', en:'Stretching (10 min)',          nl:'Stretching (10 min)',         why_en:'prevents injury and flushes lactic acid for faster physical recovery',      why_nl:'voorkomt blessures en versnelt fysiek herstel' },
    };
    const pick = isStress || lowMood ? 'breathing'
      : isFocus ? 'focus'
      : isEnergy ? 'wimhof'
      : isStrength ? 'powerpose'
      : (isSleep || lowSleep) ? 'meditation'
      : highMental ? 'bodyscan'
      : highPhysical ? 'stretching'
      : 'gratitude';
    const ex = exOptions[pick];
    const done = exDone.has(pick);
    msgs.push({
      icon: ex.icon,
      cat:  nl ? 'AANBEVOLEN OEFENING' : 'RECOMMENDED EXERCISE',
      msg:  nl
        ? (done ? `✓ Je deed al **${ex.nl}** deze week — doe het opnieuw voor extra effect.` : `Doe **${ex.nl}** vandaag.`)
        : (done ? `✓ You already did **${ex.en}** this week — repeat it for compounding effect.` : `Do **${ex.en}** today.`),
      basis: nl ? `Waarom: het ${ex.why_nl}` : `Why: it ${ex.why_en}`,
    });

    // ── 3. Rest & recovery ────────────────────────────────────────────────────
    if (lowSleep) {
      msgs.push(nl
        ? { icon:'🌙', cat:'HERSTEL ADVIES', msg:`Je gemiddelde slaapscore is **${avgSleep?.toFixed(1)}/10** — te laag voor optimaal functioneren. Plan vandaag een pauze in en vermijd schermen na 21:00.`, basis:'Op basis van: slaaplog afgelopen 7 dagen' }
        : { icon:'🌙', cat:'RECOVERY ADVICE', msg:`Your average sleep score is **${avgSleep?.toFixed(1)}/10** — too low for optimal performance. Schedule a break today and avoid screens after 9 PM.`, basis:'Based on: sleep log last 7 days' }
      );
    } else if (highMental && !isStress) {
      msgs.push(nl
        ? { icon:'🌿', cat:'HERSTEL ADVIES', msg:`Je agenda heeft **hoge mentale belasting**. Bouw minimaal één pauze van 10 min in vandaag — je brein presteert beter na echte rust.`, basis:'Op basis van: mentale belasting in je weekplanning' }
        : { icon:'🌿', cat:'RECOVERY ADVICE', msg:`Your schedule shows **high mental load**. Build in at least one 10-min break today — your brain performs better after genuine rest.`, basis:'Based on: mental load in your week schedule' }
      );
    } else if (highPhysical) {
      msgs.push(nl
        ? { icon:'🤸', cat:'HERSTEL ADVIES', msg:`Je hebt **veel fysieke activiteit** gepland. Voeg 10 min stretching toe na je training om blessures te voorkomen en herstel te versnellen.`, basis:'Op basis van: fysieke belasting in je weekplanning' }
        : { icon:'🤸', cat:'RECOVERY ADVICE', msg:`You have **high physical activity** scheduled. Add 10 min of stretching after training to prevent injury and speed recovery.`, basis:'Based on: physical load in your week schedule' }
      );
    } else {
      msgs.push(nl
        ? { icon:'🌿', cat:'HERSTEL ADVIES', msg:`Je belasting ziet er **goed gebalanceerd** uit. Houd je rustmomenten bewust en je presteert de hele week door op topniveau.`, basis:'Op basis van: belastingsanalyse weekplanning' }
        : { icon:'🌿', cat:'RECOVERY ADVICE', msg:`Your load looks **well balanced**. Keep your rest moments intentional and you'll perform at peak level all week.`, basis:'Based on: load analysis of week schedule' }
      );
    }

    // ── 4. Game recommendation ────────────────────────────────────────────────
    const gameOptions = nl ? {
      breath:   { icon:'🌬️', name:'Ademspel',    why:'zet stress om in rust via geleide ademritmes — ideaal voor jouw intentie' },
      stroop:   { icon:'🔴', name:'Stroop Spel',  why:'is de gouden standaard voor selectieve aandacht en mentale snelheid' },
      reaction: { icon:'⚡', name:'Reactiespel',  why:'scherpt reflexen en traint je alertheidsysteem in 3 minuten' },
      sequence: { icon:'🔢', name:'Sequentiespel',why:'bouwt cognitieve weerbaarheid via werkgeheugendruk' },
      memory:   { icon:'🃏', name:'Geheugenspel', why:'versterkt geheugen en concentratie in 3–5 minuten' },
    } : {
      breath:   { icon:'🌬️', name:'Breath Pacer', why:'turns stress into calm through guided breathing rhythms — perfect for your intention' },
      stroop:   { icon:'🔴', name:'Stroop Game',   why:'is the gold standard for selective attention and mental speed' },
      reaction: { icon:'⚡', name:'Reaction Game', why:'sharpens reflexes and trains your alertness system in 3 minutes' },
      sequence: { icon:'🔢', name:'Sequence Game', why:'builds cognitive resilience through working-memory pressure' },
      memory:   { icon:'🃏', name:'Memory Game',   why:'strengthens recall and concentration in 3–5 minutes' },
    };
    const gamePick = isStress || lowMood ? 'breath'
      : isFocus ? 'stroop'
      : isEnergy ? 'reaction'
      : isStrength ? 'sequence'
      : 'memory';
    const game = gameOptions[gamePick];
    msgs.push({
      icon: game.icon,
      cat:  nl ? 'SPEL VAN DE WEEK' : 'GAME OF THE WEEK',
      msg:  nl ? `Speel het **${game.name}** minstens 3× deze week.` : `Play the **${game.name}** at least 3× this week.`,
      basis: nl ? `Waarom: het ${game.why}` : `Why: it ${game.why}`,
    });

    return msgs;
  }, [intention, weekAgenda, datedAgenda, sleepLog, moodLog, exHistory, nl]);

  // ── Empty state — coach asks for intention ────────────────────────────────
  if (!intention) {
    return (
      <>
        {/* Coach prompt card */}
        <div style={{
          opacity: shown ? 1 : 0,
          transform: shown ? 'translateY(0)' : 'translateY(18px)',
          transition: 'opacity 0.55s ease 100ms, transform 0.55s cubic-bezier(0.34,1.1,0.64,1) 100ms',
          background: _T.bg3,
          border: `1px solid ${_T.cardBorderTop}`,
          borderRadius: 14,
          padding: '18px 16px',
          marginBottom: 10,
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position:'absolute',top:0,left:0,width:2,height:'100%',background:`linear-gradient(180deg,${_T.grad1},${_T.grad2})` }}/>
          <div style={{ position:'absolute',top:0,right:0,width:80,height:'100%',background:`radial-gradient(ellipse at top right,${_T.accent}09,transparent)`,pointerEvents:'none' }}/>
          <div style={{ paddingLeft: 12 }}>
            <div style={{ display:'flex',alignItems:'center',gap:7,marginBottom:10 }}>
              <span style={{ fontSize:16 }}>✦</span>
              <span style={{ fontSize:9,color:_T.accent,letterSpacing:3,fontWeight:700 }}>SENSE LIFTING COACH</span>
            </div>
            <div style={{ color:'#b0c0d8',fontSize:14,lineHeight:1.65,marginBottom:4,fontFamily:'Georgia,serif' }}>
              {nl ? 'Wat wil jij bereiken deze week?' : 'What do you want to achieve this week?'}
            </div>
            <div style={{ color:`${_T.accent}80`,fontSize:11,lineHeight:1.5,marginBottom:14,fontStyle:'italic' }}>
              {nl
                ? 'Stel je intentie in en ik begeleid je met de juiste oefeningen, spellen en rustmomenten.'
                : "Set your intention and I'll coach you with the right exercises, games and recovery."}
            </div>
            <button onClick={() => setPickerOpen(true)} style={{
              background:`linear-gradient(135deg,${_T.grad1},${_T.grad2})`,
              border:'none',borderRadius:10,padding:'10px 20px',
              color:'#0a1220',fontSize:13,fontWeight:700,cursor:'pointer',letterSpacing:0.5,
            }}>
              {nl ? '✦ Stel mijn intentie in' : '✦ Set my intention'}
            </button>
          </div>
        </div>

        {/* Full-screen WeeklyIntention overlay */}
        {pickerOpen && (
          <div style={{ position:'fixed',inset:0,zIndex:9999,overflow:'hidden',display:'flex',flexDirection:'column' }}>
            <div style={{ flex:1,overflowY:'auto' }}>
              <WeeklyIntention
                lang={lang}
                onDone={value => { setIntention(value); setPickerOpen(false); }}
              />
            </div>
            <button
              onClick={() => setPickerOpen(false)}
              style={{ position:'fixed',top:14,right:16,background:'rgba(8,14,26,0.7)',border:`1px solid ${_T.cardBorderBot}`,borderRadius:20,color:C.silver,fontSize:16,cursor:'pointer',zIndex:10000,lineHeight:1,padding:'4px 9px' }}>
              ✕
            </button>
          </div>
        )}
      </>
    );
  }

  if (messages.length === 0) return null;

  const dismiss = (i) => setDismissed(prev => {
    const next = { ...prev, [i]: true };
    try { localStorage.setItem(getWeekKey(), JSON.stringify(next)); } catch {}
    return next;
  });

  return (
    <div style={{ marginBottom: 10 }}>
      {messages.map((msg, i) => {
        const isDismissed = !!dismissed[i];
        return (
          <div key={i} style={{
            maxHeight: isDismissed ? 0 : 200,
            marginBottom: isDismissed ? 0 : 8,
            overflow: 'hidden',
            transition: `max-height 0.38s ease ${isDismissed ? '0s' : `${i * 190}ms`}, margin-bottom 0.38s ease`,
          }}>
            <div
              onClick={() => dismiss(i)}
              onTouchStart={e => { touchStartY.current[i] = e.touches[0].clientY; }}
              onTouchEnd={e => {
                const dy = touchStartY.current[i] - e.changedTouches[0].clientY;
                if (dy > 30) dismiss(i);
              }}
              style={{
                opacity: isDismissed ? 0 : shown ? 1 : 0,
                transform: isDismissed ? 'translateY(-14px)' : shown ? 'translateY(0)' : 'translateY(18px)',
                transition: isDismissed
                  ? 'opacity 0.28s ease, transform 0.28s ease'
                  : `opacity 0.55s ease ${i * 190}ms, transform 0.55s cubic-bezier(0.34,1.1,0.64,1) ${i * 190}ms`,
                background: _T.bg3,
                border: `1px solid ${_T.cardBorderBot}`,
                borderTop: `1.5px solid ${i === 0 ? _T.cardBorderTop : _T.cardBorderBot}`,
                borderRadius: 14,
                padding: '14px 16px',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                userSelect: 'none',
              }}>
              {/* Left accent bar */}
              <div style={{ position:'absolute',top:0,left:0,width:2,height:'100%',background:`linear-gradient(180deg,${_T.grad1},${_T.grad2})` }}/>
              {/* Subtle corner glow */}
              <div style={{ position:'absolute',top:0,right:0,width:80,height:'100%',background:`radial-gradient(ellipse at top right,${_T.accent}09,transparent)`,pointerEvents:'none' }}/>
              {/* Dismiss hint */}
              <div style={{ position:'absolute',top:8,right:12,fontSize:9,color:`${_T.accent}40`,letterSpacing:1 }}>✕</div>
              <div style={{ paddingLeft: 12 }}>
                <div style={{ display:'flex',alignItems:'center',gap:7,marginBottom:7 }}>
                  <span style={{ fontSize:15 }}>{msg.icon}</span>
                  <span style={{ fontSize:9,color:_T.accent,letterSpacing:3,fontWeight:700 }}>{msg.cat}</span>
                </div>
                <div style={{ color:'#b0c0d8',fontSize:13,lineHeight:1.65,marginBottom:6 }}>{bold(msg.msg)}</div>
                <div style={{ fontSize:10,color:`${_T.accent}70`,fontStyle:'italic',letterSpacing:0.3 }}>{msg.basis}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main screen ────────────────────────────────────────────────────────────────
function WeekScreen({ lang, weekIntention, setWeekIntention, weekAgenda, setWeekAgenda, datedAgenda, setDatedAgenda, currentUserId, userName }) {
  const nl = lang === 'nl';
  const DAYS_NL  = ['Ma','Di','Wo','Do','Vr','Za','Zo'];
  const DAYS_EN  = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const FULL_NL  = ['Maandag','Dinsdag','Woensdag','Donderdag','Vrijdag','Zaterdag','Zondag'];
  const FULL_EN  = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
  const days     = nl ? DAYS_NL : DAYS_EN;
  const daysFull = nl ? FULL_NL : FULL_EN;

  // Current week's Monday date
  const weekStart = useMemo(() => {
    const now = new Date();
    const dow = now.getDay() === 0 ? 6 : now.getDay() - 1;
    const d = new Date(now); d.setDate(d.getDate() - dow); d.setHours(0,0,0,0);
    return d;
  }, []);

  const [selDay, setSelDay] = useState(new Date().getDay() === 0 ? 6 : new Date().getDay() - 1);
  const [view, setView] = useState('week'); // 'week' | 'date' | 'all'
  const [pickedDate, setPickedDate] = useState(() => toDateStr(new Date()));
  const [showAdd, setShowAdd] = useState(null);
  const [selItem, setSelItem] = useState(null);
  const [dur, setDur] = useState(60);
  const [startTime, setStartTime] = useState('09:00');
  const [editTarget, setEditTarget] = useState(null); // item being edited in "all" view

  // Custom appointment state
  const [customTitle, setCustomTitle] = useState('');
  const [customDesc, setCustomDesc] = useState('');
  const [customMental, setCustomMental] = useState(5);
  const [customPhysical, setCustomPhysical] = useState(2);
  const [customStart, setCustomStart] = useState('09:00');
  const [customEnd, setCustomEnd] = useState('10:00');
  const [customDay, setCustomDay] = useState(null);
  const [useSpecificDate, setUseSpecificDate] = useState(false);
  const [specificDate, setSpecificDate] = useState(() => toDateStr(new Date()));

  // Invite modal state
  const [inviteTarget, setInviteTarget] = useState(null); // { item, dateKey, dateLabel }

  const selDayDate = useMemo(() => {
    const d = new Date(weekStart); d.setDate(d.getDate() + selDay);
    return toDateStr(d);
  }, [weekStart, selDay]);

  const calcDurFromTimes = (s, e) => {
    const [sh, sm] = s.split(':').map(Number);
    const [eh, em] = e.split(':').map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 1440;
    return Math.max(5, mins);
  };

  const ACTIVITIES = [
    { id:'work',      label:'Work',       labelNL:'Werk',       mental:75,physical:15,icon:'💼',type:'activity' },
    { id:'school',    label:'School',     labelNL:'School',     mental:80,physical:10,icon:'📚',type:'activity' },
    { id:'sport',     label:'Sport',      labelNL:'Sport',      mental:30,physical:85,icon:'🏋️',type:'activity' },
    { id:'social',    label:'Social',     labelNL:'Sociaal',    mental:55,physical:20,icon:'👥',type:'activity' },
    { id:'household', label:'Household',  labelNL:'Huishouden', mental:25,physical:50,icon:'🏠',type:'activity' },
    { id:'walking',   label:'Walking',    labelNL:'Wandelen',   mental:15,physical:40,icon:'🚶',type:'activity' },
    { id:'creative',  label:'Creative',   labelNL:'Creatief',   mental:60,physical:5, icon:'🎨',type:'activity' },
  ];
  const REST_TYPES = [
    { id:'breathing', label:'Breathing',  labelNL:'Ademhaling', duration:5, mental:35,physical:10,icon:'🌬️',type:'rest' },
    { id:'meditation',label:'Meditation', labelNL:'Meditatie',  duration:10,mental:45,physical:5, icon:'🧘',type:'rest' },
    { id:'stretching',label:'Stretching', labelNL:'Stretching', duration:10,mental:10,physical:25,icon:'🤸',type:'rest' },
    { id:'break',     label:'Short break',labelNL:'Korte pauze',duration:5, mental:20,physical:10,icon:'☕',type:'rest' },
  ];

  // All appointments across all stores, sorted by date+time
  const allAppointments = useMemo(() => {
    const result = [];
    // From weekAgenda (day-index 0-6 = this week Mon-Sun)
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart); d.setDate(d.getDate() + i);
      const dateKey = toDateStr(d);
      (weekAgenda[i] || []).forEach(item => result.push({ ...item, _src:'week', _dayIdx:i, _dateKey:dateKey }));
    }
    // From datedAgenda (any date)
    Object.entries(datedAgenda || {}).forEach(([dateKey, items]) => {
      (items || []).forEach(item => result.push({ ...item, _src:'dated', _dateKey:dateKey }));
    });
    return result.sort((a, b) => {
      const dc = a._dateKey.localeCompare(b._dateKey);
      if (dc !== 0) return dc;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
  }, [weekAgenda, datedAgenda, weekStart]);

  // Items for "pick date" view
  const pickedDateItems = useMemo(() => {
    // Check weekAgenda if picked date falls in current week
    const pickedD = new Date(pickedDate + 'T00:00:00');
    const wsMon = new Date(weekStart);
    const diff = Math.round((pickedD - wsMon) / 86400000);
    const weekItems = (diff >= 0 && diff <= 6) ? (weekAgenda[diff] || []).map(i => ({...i, _src:'week', _dayIdx:diff, _dateKey:pickedDate})) : [];
    const dated = ((datedAgenda || {})[pickedDate] || []).map(i => ({...i, _src:'dated', _dateKey:pickedDate}));
    return [...weekItems, ...dated].sort((a,b) => (a.startTime||'').localeCompare(b.startTime||''));
  }, [pickedDate, weekAgenda, datedAgenda, weekStart]);

  // Merge weekAgenda + datedAgenda items for the selected day
  const weekItems  = weekAgenda[selDay] || [];
  const datedItems = (datedAgenda || {})[selDayDate] || [];
  const sorted = [...weekItems.map(i => ({...i, _src:'week'})), ...datedItems.map(i => ({...i, _src:'dated', _dateKey: selDayDate}))]
    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));

  // Use picked-date items when in date view
  const displayItems = view === 'date' ? pickedDateItems : sorted;

  const { mental, physical, balance } = calcLoad([...weekItems, ...datedItems]);

  const removeItem = (item) => {
    if (item._src === 'dated') {
      const key = item._dateKey || selDayDate;
      const updated = { ...(datedAgenda || {}) };
      updated[key] = (updated[key] || []).filter(i => i.id !== item.id);
      setDatedAgenda?.(updated);
    } else {
      setWeekAgenda({ ...weekAgenda, [selDay]: (weekAgenda[selDay] || []).filter(i => i.id !== item.id) });
    }
  };

  const updateItemAttendees = (item, token, channel) => {
    const attendee = { channel, status: 'pending', invitedAt: Date.now(), token };
    const newAttendees = [...(item.attendees || []), attendee];
    if (item._src === 'dated') {
      const key = item._dateKey || selDayDate;
      const updated = { ...(datedAgenda || {}) };
      updated[key] = (updated[key] || []).map(i => i.id === item.id ? { ...i, attendees: newAttendees } : i);
      setDatedAgenda?.(updated);
    } else {
      setWeekAgenda(prev => ({
        ...prev,
        [selDay]: (prev[selDay] || []).map(i => i.id === item.id ? { ...i, attendees: newAttendees } : i),
      }));
    }
  };

  const addItem = () => {
    if (showAdd === 'custom') {
      if (!customTitle.trim()) return;
      const item = {
        id: Date.now(), type: 'custom', icon: '📌',
        label: customTitle.trim(), labelNL: customTitle.trim(),
        description: customDesc.trim(),
        startTime: customStart, endTime: customEnd,
        duration: calcDurFromTimes(customStart, customEnd),
        mental: customMental * 10, physical: customPhysical * 10,
        attendees: [],
      };
      if (useSpecificDate) {
        const key = specificDate;
        setDatedAgenda?.(prev => ({ ...prev, [key]: [...((prev || {})[key] || []), item] }));
      } else {
        const targetDay = customDay !== null ? customDay : selDay;
        setWeekAgenda({ ...weekAgenda, [targetDay]: [...(weekAgenda[targetDay] || []), item] });
        if (targetDay !== selDay) setSelDay(targetDay);
      }
      setCustomTitle(''); setCustomDesc(''); setCustomMental(5); setCustomPhysical(2);
      setCustomStart('09:00'); setCustomEnd('10:00'); setCustomDay(null); setShowAdd(null);
      setUseSpecificDate(false);
      return;
    }
    if (showAdd === 'exercise') {
      if (!selItem) return;
      const ex = EXERCISES.find(e => e.id === selItem);
      if (!ex) return;
      const item = { id: Date.now(), type: 'exercise', icon: ex.icon, label: ex.labelEN, labelNL: ex.label, exId: ex.id, startTime, duration: dur, mental: Math.abs(ex.mental), physical: Math.abs(ex.physical), isExercise: true };
      setWeekAgenda({ ...weekAgenda, [selDay]: [...(weekAgenda[selDay] || []), item] });
      setShowAdd(null); setSelItem(null); setDur(15); setStartTime('09:00');
      return;
    }
    if (!selItem) return;
    const isRest = showAdd === 'rest';
    const found = (isRest ? REST_TYPES : ACTIVITIES).find(x => x.id === selItem);
    if (!found) return;
    setWeekAgenda({ ...weekAgenda, [selDay]: [...(weekAgenda[selDay] || []), { ...found, duration: dur, startTime, id: Date.now() }] });
    setShowAdd(null); setSelItem(null); setDur(60); setStartTime('09:00');
  };

  const itemColor = (item) => {
    if (item.type === 'exercise') return '#7B6FD0';
    if (item.type === 'rest') return C.teal;
    if (item.type === 'custom') return _T.accent;
    return _T.accent;
  };

  // Date label for the selected day (or specific date)
  const selDayLabel = useMemo(() => {
    if (useSpecificDate) {
      const d = new Date(specificDate + 'T00:00:00');
      return d.toLocaleDateString(nl ? 'nl-NL' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    }
    return daysFull[selDay];
  }, [useSpecificDate, specificDate, selDay, daysFull, nl]);

  return (
    <div className="fadeUp">
      {/* Intention coach — shows prompt when empty, coaching cards when set */}
      {weekIntention ? (
        <>
          {/* Intention quote banner */}
          <div style={{ background:_T.bg3,border:`1px solid ${_T.cardBorderTop}`,borderRadius:14,padding:'12px 16px',position:'relative',overflow:'hidden',marginBottom:8 }}>
            <div style={{ position:'absolute',top:0,left:0,width:3,height:'100%',background:`linear-gradient(180deg,${_T.grad1},${_T.grad2})` }}/>
            <div style={{ paddingLeft:12,display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:9,color:_T.accent,letterSpacing:3,fontWeight:700,marginBottom:4 }}>{nl?'WEEKINTENTIE':'WEEK INTENTION'}</div>
                <div style={{ color:C.silver2,fontSize:13,fontStyle:'italic',lineHeight:1.5 }}>"{weekIntention}"</div>
              </div>
              <button onClick={() => setWeekIntention('')} title={nl?'Wijzig intentie':'Change intention'}
                style={{ background:'none',border:'none',color:`${C.silver}55`,cursor:'pointer',fontSize:11,padding:'2px 0',flexShrink:0,marginTop:2 }}>✕</button>
            </div>
          </div>
          <IntentionCoach
            intention={weekIntention}
            setIntention={setWeekIntention}
            weekAgenda={weekAgenda}
            datedAgenda={datedAgenda}
            lang={lang}
          />
        </>
      ) : (
        <IntentionCoach
          intention=""
          setIntention={setWeekIntention}
          weekAgenda={weekAgenda}
          datedAgenda={datedAgenda}
          lang={lang}
        />
      )}

      {/* Top nav: day tabs + date picker + all list */}
      <div style={{ display:'flex',gap:4,marginBottom:14,overflowX:'auto',paddingBottom:2,alignItems:'stretch' }}>
        {/* Day tabs */}
        {days.map((d, i) => {
          const wd = new Date(weekStart); wd.setDate(wd.getDate() + i);
          const wdKey = toDateStr(wd);
          const allItems = [...(weekAgenda[i] || []), ...((datedAgenda || {})[wdKey] || [])];
          const { burnout: b } = calcLoad(allItems);
          const dot = b === 'high' ? C.red : b === 'medium' ? C.orange : allItems.length ? C.teal : _T.bg4;
          const active = view === 'week' && selDay === i;
          return (
            <button key={i} onClick={() => { setSelDay(i); setView('week'); setShowAdd(null); }}
              style={{ flex:'0 0 auto',width:40,background:active?`${_T.accent}18`:_T.bg3,borderTop:`1.5px solid ${active?_T.cardBorderTop:_T.cardBorderBot}`,borderBottom:`1px solid ${_T.cardBorderBot}`,borderLeft:'1px solid rgba(255,255,255,0.03)',borderRight:'1px solid rgba(255,255,255,0.03)',borderRadius:12,padding:'7px 4px',cursor:'pointer',textAlign:'center' }}>
              <div style={{ fontSize:13,color:active?_T.accent:C.silver,fontWeight:active?700:400 }}>{d}</div>
              <div style={{ width:5,height:5,borderRadius:'50%',background:dot,margin:'4px auto 0' }}/>
            </button>
          );
        })}

        {/* Divider */}
        <div style={{ width:1,background:_T.cardBorderBot,flexShrink:0,margin:'4px 2px' }}/>

        {/* Pick date button */}
        <button onClick={() => { setView('date'); setShowAdd(null); }}
          style={{ flex:'0 0 auto',width:40,background:view==='date'?`${C.orange}20`:_T.bg3,borderTop:`1.5px solid ${view==='date'?C.orange:_T.cardBorderBot}`,borderBottom:`1px solid ${_T.cardBorderBot}`,borderLeft:'1px solid rgba(255,255,255,0.03)',borderRight:'1px solid rgba(255,255,255,0.03)',borderRadius:12,padding:'7px 4px',cursor:'pointer',textAlign:'center' }}>
          <div style={{ fontSize:14 }}>📅</div>
          <div style={{ fontSize:7,color:view==='date'?C.orange:C.silver,marginTop:2,fontWeight:view==='date'?700:400,whiteSpace:'nowrap' }}>{nl?'Datum':'Date'}</div>
        </button>

        {/* All appointments button */}
        <button onClick={() => { setView('all'); setShowAdd(null); setEditTarget(null); }}
          style={{ flex:'0 0 auto',minWidth:40,background:view==='all'?`${_T.accent}20`:_T.bg3,borderTop:`1.5px solid ${view==='all'?_T.accent:_T.cardBorderBot}`,borderBottom:`1px solid ${_T.cardBorderBot}`,borderLeft:'1px solid rgba(255,255,255,0.03)',borderRight:'1px solid rgba(255,255,255,0.03)',borderRadius:12,padding:'7px 6px',cursor:'pointer',textAlign:'center',position:'relative' }}>
          <div style={{ fontSize:14 }}>📋</div>
          <div style={{ fontSize:7,color:view==='all'?_T.accent:C.silver,marginTop:2,fontWeight:view==='all'?700:400,whiteSpace:'nowrap' }}>{nl?'Alles':'All'}</div>
          {allAppointments.length > 0 && (
            <div style={{ position:'absolute',top:3,right:3,background:_T.accent,borderRadius:'50%',width:14,height:14,display:'flex',alignItems:'center',justifyContent:'center',fontSize:7,color:'#0a1220',fontWeight:700 }}>
              {allAppointments.length}
            </div>
          )}
        </button>
      </div>

      {/* Date picker view header */}
      {view === 'date' && (
        <div style={{ marginBottom:14 }}>
          <input type="date" value={pickedDate} onChange={e => setPickedDate(e.target.value)}
            style={{ width:'100%',background:_T.bg3,border:`1.5px solid ${C.orange}`,color:C.white,borderRadius:12,padding:'10px 14px',fontSize:14,boxSizing:'border-box',marginBottom:10 }}/>
          <div style={{ fontFamily:'Georgia,serif',fontSize:18,color:C.white }}>
            {new Date(pickedDate+'T00:00:00').toLocaleDateString(nl?'nl-NL':'en-US',{weekday:'long',day:'numeric',month:'long'})}
          </div>
        </div>
      )}

      {/* Week day header */}
      {view === 'week' && (
        <div style={{ fontFamily:'Georgia,serif',fontSize:18,color:C.white,marginBottom:12 }}>{daysFull[selDay]}</div>
      )}

      {/* ── ALL APPOINTMENTS VIEW ── */}
      {view === 'all' && (
        <div>
          <div style={{ fontFamily:'Georgia,serif',fontSize:18,color:C.white,marginBottom:12 }}>
            {nl?'Alle afspraken':'All appointments'}
            <span style={{ fontSize:12,color:C.silver,fontWeight:400,marginLeft:8,fontFamily:'Inter,system-ui' }}>({allAppointments.length})</span>
          </div>
          {allAppointments.length === 0 ? (
            <div style={{ ...s_GC({textAlign:'center',padding:28}),color:C.silver,fontSize:13 }}>
              {nl?'Geen afspraken gepland.':'No appointments scheduled.'}
            </div>
          ) : (
            <div style={s_GC()}>
              {allAppointments.map((item, idx) => {
                const dateLabel = new Date(item._dateKey+'T00:00:00').toLocaleDateString(nl?'nl-NL':'en-US',{weekday:'short',day:'numeric',month:'short'});
                const col = item.type==='exercise'?'#7B6FD0':item.type==='rest'?C.teal:_T.accent;
                const isEditing = editTarget?.id === item.id && editTarget?._dateKey === item._dateKey;
                return (
                  <div key={item._dateKey+'-'+item.id} style={{ borderBottom:idx<allAppointments.length-1?`1px solid ${_T.cardBorderBot}`:'none',padding:'12px 0' }}>
                    {isEditing ? (
                      /* Inline edit form */
                      <div style={{ background:_T.bg4,borderRadius:12,padding:12,marginBottom:2 }}>
                        <div style={{ fontSize:10,color:col,letterSpacing:2,fontWeight:700,marginBottom:10 }}>✏️ {nl?'Bewerken':'Edit'}</div>
                        <input defaultValue={item.label} id={`edit-title-${item.id}`}
                          style={{ width:'100%',background:_T.bg3,border:`1px solid ${_T.cardBorderBot}`,color:C.white,borderRadius:8,padding:'8px 10px',fontSize:13,boxSizing:'border-box',marginBottom:8 }}/>
                        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8 }}>
                          <div>
                            <div style={{ fontSize:10,color:C.silver,marginBottom:3 }}>⏰ {nl?'Van':'From'}</div>
                            <input type="time" defaultValue={item.startTime||'09:00'} id={`edit-start-${item.id}`}
                              style={{ width:'100%',background:_T.bg3,border:`1px solid ${_T.cardBorderBot}`,color:C.white,borderRadius:8,padding:'8px 10px',fontSize:13,boxSizing:'border-box' }}/>
                          </div>
                          <div>
                            <div style={{ fontSize:10,color:C.silver,marginBottom:3 }}>⏰ {nl?'Tot':'Until'}</div>
                            <input type="time" defaultValue={item.endTime||''} id={`edit-end-${item.id}`}
                              style={{ width:'100%',background:_T.bg3,border:`1px solid ${_T.cardBorderBot}`,color:C.white,borderRadius:8,padding:'8px 10px',fontSize:13,boxSizing:'border-box' }}/>
                          </div>
                        </div>
                        <div style={{ display:'flex',gap:8 }}>
                          <button onClick={() => {
                            const title = document.getElementById(`edit-title-${item.id}`)?.value || item.label;
                            const start = document.getElementById(`edit-start-${item.id}`)?.value || item.startTime;
                            const end   = document.getElementById(`edit-end-${item.id}`)?.value   || item.endTime;
                            const updated = { ...item, label:title, labelNL:title, startTime:start, endTime:end };
                            if (item._src === 'dated') {
                              setDatedAgenda(prev => ({ ...prev, [item._dateKey]: (prev[item._dateKey]||[]).map(i => i.id===item.id?updated:i) }));
                            } else {
                              setWeekAgenda(prev => ({ ...prev, [item._dayIdx]: (prev[item._dayIdx]||[]).map(i => i.id===item.id?updated:i) }));
                            }
                            setEditTarget(null);
                          }} style={{ flex:1,background:`linear-gradient(135deg,${_T.grad1},${_T.grad2})`,border:'none',borderRadius:8,padding:'8px',color:'#0a1220',fontSize:12,fontWeight:700,cursor:'pointer' }}>
                            {nl?'Opslaan':'Save'}
                          </button>
                          <button onClick={() => setEditTarget(null)} style={{ background:'transparent',border:`1px solid ${_T.cardBorderBot}`,borderRadius:8,padding:'8px 12px',color:C.silver,fontSize:12,cursor:'pointer' }}>
                            {nl?'Annuleer':'Cancel'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                        <div style={{ width:32,height:32,borderRadius:10,background:`${col}15`,border:`1px solid ${col}33`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,flexShrink:0 }}>
                          {item.icon}
                        </div>
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ color:C.white,fontSize:13,fontWeight:600 }}>{item.label}</div>
                          <div style={{ display:'flex',gap:6,marginTop:2,flexWrap:'wrap',alignItems:'center' }}>
                            <span style={{ fontSize:10,color:col,fontWeight:600 }}>📅 {dateLabel}</span>
                            {item.startTime && <span style={{ fontSize:10,color:C.silver }}>⏰ {item.startTime}{item.endTime?` – ${item.endTime}`:''}</span>}
                            <span style={{ fontSize:9,color:C.silver,background:_T.bg4,padding:'1px 6px',borderRadius:10 }}>{item.duration}m</span>
                          </div>
                          {item.attendees?.length > 0 && (
                            <div style={{ display:'flex',gap:4,marginTop:4,flexWrap:'wrap' }}>
                              {item.attendees.map((a,ai) => (
                                <span key={ai} style={{ fontSize:9,padding:'1px 5px',borderRadius:6,fontWeight:600,
                                  background:a.status==='accepted'?'#4AC87A20':a.status==='declined'?'#C0505020':'#C8A84B20',
                                  color:a.status==='accepted'?'#4AC87A':a.status==='declined'?'#C05050':'#C8A84B',
                                  border:`1px solid ${a.status==='accepted'?'#4AC87A44':a.status==='declined'?'#C0505044':'#C8A84B44'}` }}>
                                  {a.status==='accepted'?'✓':a.status==='declined'?'✗':'⏳'}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <button onClick={() => setEditTarget(item)}
                          style={{ background:'none',border:'none',color:C.silver,cursor:'pointer',fontSize:16,padding:'4px',flexShrink:0 }}>✏️</button>
                        <button onClick={() => {
                          if (item._src === 'dated') {
                            setDatedAgenda(prev => ({ ...prev, [item._dateKey]: (prev[item._dateKey]||[]).filter(i=>i.id!==item.id) }));
                          } else {
                            setWeekAgenda(prev => ({ ...prev, [item._dayIdx]: (prev[item._dayIdx]||[]).filter(i=>i.id!==item.id) }));
                          }
                        }} style={{ background:'none',border:'none',color:`${C.red}66`,cursor:'pointer',fontSize:16,padding:'4px',flexShrink:0 }}>🗑</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Balance gauges — only in week/date view */}
      {view !== 'all' && (
      <div style={{ display:'flex',gap:8,justifyContent:'space-around',...s_GC(),marginBottom:10 }}>
        <RingGauge value={mental}   label={nl?'Mentaal':'Mental'}   color={C.purple}/>
        <RingGauge value={physical} label={nl?'Fysiek':'Physical'}  color={C.teal}/>
        <RingGauge value={balance}  label={nl?'Balans':'Balance'}   color={_T.accent}/>
      </div>
      )}

      {/* Add buttons */}
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:6,marginBottom:10 }}>
        {[
          { id:'activity',label:nl?'Activiteit':'Activity',icon:'💼',color:_T.accent },
          { id:'rest',    label:nl?'Rust':'Rest',           icon:'🌿',color:C.teal },
          { id:'exercise',label:nl?'Oefening':'Exercise',   icon:'🧘',color:'#7B6FD0' },
          { id:'custom',  label:nl?'Afspraak':'Appointment',icon:'📌',color:C.orange },
        ].map(btn => (
          <button key={btn.id} onClick={() => { setShowAdd(btn.id); setSelItem(null); setDur(btn.id==='rest'?15:btn.id==='exercise'?15:60); setStartTime('09:00'); }}
            style={{ background:showAdd===btn.id?`${btn.color}20`:_T.bg4,border:`1px solid ${showAdd===btn.id?btn.color:btn.color+'33'}`,borderRadius:12,padding:'8px 4px',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:3 }}>
            <span style={{ fontSize:16 }}>{btn.icon}</span>
            <span style={{ fontSize:9,color:showAdd===btn.id?btn.color:C.silver,fontWeight:showAdd===btn.id?700:400,whiteSpace:'nowrap' }}>{btn.label}</span>
          </button>
        ))}
      </div>

      {/* Add forms — activity / rest / exercise */}
      {showAdd && showAdd !== 'custom' && (
        <div style={s_GC({ marginBottom:10 })}>
          {showAdd === 'exercise' && (
            <>
              <div style={{ fontSize:11,color:C.silver,marginBottom:8,fontWeight:600 }}>{nl?'Kies een oefening:':'Choose an exercise:'}</div>
              <div style={{ display:'flex',flexWrap:'wrap',gap:6,marginBottom:12 }}>
                {EXERCISES.map(ex => (
                  <button key={ex.id} onClick={() => { playSound('filter'); setSelItem(ex.id); setDur(ex.duration); }}
                    style={{ ...s_PL(selItem===ex.id,'#7B6FD0'),display:'flex',alignItems:'center',gap:4 }}>
                    <span>{ex.icon}</span><span>{nl?ex.label:ex.labelEN}</span>
                    <span style={{ fontSize:9,opacity:0.7 }}>{ex.duration}m</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {(showAdd === 'activity' || showAdd === 'rest') && (
            <div style={{ display:'flex',flexWrap:'wrap',gap:4,marginBottom:10 }}>
              {(showAdd === 'rest' ? REST_TYPES : ACTIVITIES).map(a => (
                <button key={a.id} style={s_PL(selItem===a.id,showAdd==='rest'?C.teal:undefined)} onClick={() => { playSound('filter'); setSelItem(a.id); }}>
                  {a.icon} {nl?(a.labelNL||a.label):a.label}
                </button>
              ))}
            </div>
          )}
          <div style={{ display:'flex',gap:10,marginBottom:10 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11,color:C.silver,marginBottom:4 }}>{nl?'Starttijd':'Start time'}</div>
              <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={s_TI({ padding:'9px 12px' })}/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11,color:C.silver,marginBottom:4 }}>{nl?'Duur (min)':'Duration (min)'}</div>
              <input type="number" min={5} max={480} value={dur} onChange={e => setDur(+e.target.value)} style={s_TI({ padding:'9px 12px' })}/>
            </div>
          </div>
          <div style={{ display:'flex',gap:8 }}>
            <button style={s_PB()} onClick={addItem} disabled={!selItem}>{nl?'Toevoegen':'Add'}</button>
            <button style={s_GB()} onClick={() => setShowAdd(null)}>✕</button>
          </div>
        </div>
      )}

      {/* Custom appointment form */}
      {showAdd === 'custom' && (
        <div style={s_GC({ marginBottom:10 })}>
          <div style={{ fontSize:12,color:C.orange,letterSpacing:3,fontWeight:600,marginBottom:12 }}>📌 {nl?'AFSPRAAK INPLANNEN':'PLAN APPOINTMENT'}</div>

          <input value={customTitle} onChange={e => setCustomTitle(e.target.value)}
            placeholder={nl?'Titel *':'Title *'}
            style={s_TI({ marginBottom:10,padding:'10px 12px' })}/>

          <textarea value={customDesc} onChange={e => setCustomDesc(e.target.value)}
            placeholder={nl?'Beschrijving (optioneel)':'Description (optional)'}
            style={s_TI({ minHeight:56,resize:'none',marginBottom:12,padding:'10px 12px',fontSize:13 })}/>

          {/* This week / Pick date toggle */}
          <div style={{ display:'flex',gap:8,marginBottom:12 }}>
            <button onClick={() => setUseSpecificDate(false)} style={{ ...s_PL(!useSpecificDate,C.orange),flex:1,fontSize:12 }}>
              {nl?'Deze week':'This week'}
            </button>
            <button onClick={() => setUseSpecificDate(true)} style={{ ...s_PL(useSpecificDate,C.orange),flex:1,fontSize:12 }}>
              📅 {nl?'Kies datum':'Pick date'}
            </button>
          </div>

          {/* Week day selector */}
          {!useSpecificDate && (
            <>
              <div style={{ fontSize:11,color:C.silver,marginBottom:6,fontWeight:600 }}>{nl?'Dag':'Day'}</div>
              <div style={{ display:'flex',gap:4,marginBottom:12,flexWrap:'wrap' }}>
                {days.map((d, i) => (
                  <button key={i} onClick={() => setCustomDay(i === selDay ? null : i)}
                    style={{ ...s_PL((customDay ?? selDay) === i, C.orange),margin:'2px',padding:'5px 10px',fontSize:11 }}>
                    {d}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Specific date picker */}
          {useSpecificDate && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11,color:C.silver,marginBottom:6,fontWeight:600 }}>📅 {nl?'Datum':'Date'}</div>
              <input type="date" value={specificDate} min={toDateStr(new Date())}
                onChange={e => setSpecificDate(e.target.value)}
                style={s_TI({ padding:'9px 12px' })}/>
            </div>
          )}

          {/* Time range */}
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12 }}>
            <div>
              <div style={{ fontSize:11,color:C.silver,marginBottom:4 }}>⏰ {nl?'Van':'From'}</div>
              <input type="time" value={customStart} onChange={e => setCustomStart(e.target.value)} style={s_TI({ padding:'9px 12px' })}/>
            </div>
            <div>
              <div style={{ fontSize:11,color:C.silver,marginBottom:4 }}>⏰ {nl?'Tot':'Until'}</div>
              <input type="time" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={s_TI({ padding:'9px 12px' })}/>
            </div>
          </div>
          <div style={{ fontSize:11,color:_T.accent,marginBottom:12 }}>
            ⏱ {nl?'Duur':'Duration'}: <strong>{calcDurFromTimes(customStart,customEnd)} {nl?'minuten':'minutes'}</strong>
          </div>

          {/* Energy sliders */}
          {[
            { label:`🧠 ${nl?'Mentale energie':'Mental energy'}`,val:customMental,set:setCustomMental,color:C.purple,max:'Exhausting' },
            { label:`💪 ${nl?'Fysieke energie':'Physical energy'}`,val:customPhysical,set:setCustomPhysical,color:C.teal,max:'Exhausting' },
          ].map((sl, i) => (
            <div key={i} style={{ marginBottom:i===0?12:14 }}>
              <div style={{ display:'flex',justifyContent:'space-between',marginBottom:5 }}>
                <div style={{ fontSize:11,color:C.silver,fontWeight:600 }}>{sl.label}</div>
                <div style={{ fontSize:13,color:sl.color,fontWeight:700 }}>{sl.val}/10</div>
              </div>
              <input type="range" min={0} max={10} value={sl.val} onChange={e => sl.set(+e.target.value)} style={{ width:'100%',accentColor:sl.color }}/>
            </div>
          ))}

          <div style={{ display:'flex',gap:8,marginBottom:8 }}>
            <button style={s_PB({ flex:1,opacity:customTitle.trim()?1:0.4 })} onClick={addItem} disabled={!customTitle.trim()}>
              {nl?'Inplannen':'Schedule'}
            </button>
            <button style={s_GB()} onClick={() => { setShowAdd(null); setUseSpecificDate(false); setCustomTitle(''); }}>✕</button>
          </div>

          {/* Date preview */}
          {customTitle.trim() && (
            <div style={{ fontSize:11,color:C.silver,textAlign:'center',marginTop:4,fontStyle:'italic' }}>
              {nl?'Wordt ingepland op:':'Scheduled for:'} <span style={{ color:_T.accent,fontWeight:600 }}>{selDayLabel}</span>
            </div>
          )}
        </div>
      )}

      {/* Schedule list */}
      {displayItems.length > 0 && (
        <div style={s_GC()}>
          <div style={{ fontSize:12,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:10 }}>{nl?'AGENDA':'SCHEDULE'}</div>
          {displayItems.map((item, i) => {
            const dateLabel = item._src === 'dated'
              ? new Date((item._dateKey || selDayDate) + 'T00:00:00').toLocaleDateString(nl ? 'nl-NL' : 'en-US', { weekday:'short', month:'short', day:'numeric' })
              : daysFull[selDay];
            return (
              <div key={item.id} style={{ display:'flex',alignItems:'flex-start',gap:10,padding:'10px 0',borderBottom:i<displayItems.length-1?`1px solid ${_T.cardBorderBot}`:'none' }}>
                <div style={{ textAlign:'right',minWidth:44,flexShrink:0,paddingTop:2 }}>
                  <div style={{ fontSize:11,color:_T.accent,fontWeight:600 }}>{item.startTime||'--:--'}</div>
                  {item.endTime && <div style={{ fontSize:9,color:C.silver }}>{item.endTime}</div>}
                  {item._src === 'dated' && (
                    <div style={{ fontSize:8,color:C.orange,marginTop:2 }}>📅</div>
                  )}
                </div>
                <div style={{ width:32,height:32,borderRadius:10,background:`${itemColor(item)}15`,border:`1px solid ${itemColor(item)}33`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,flexShrink:0 }}>
                  {item.icon}
                </div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ display:'flex',alignItems:'center',gap:6,flexWrap:'wrap' }}>
                    <div style={{ color:C.silver2,fontSize:12,fontWeight:600 }}>{nl?(item.labelNL||item.label):item.label}</div>
                    {item.type === 'exercise' && <span style={{ fontSize:9,color:'#7B6FD0',background:'#7B6FD020',padding:'1px 6px',borderRadius:8,fontWeight:600 }}>{nl?'Oefening':'Exercise'}</span>}
                    {item.type === 'custom' && <span style={{ fontSize:9,color:C.orange,background:`${C.orange}20`,padding:'1px 6px',borderRadius:8,fontWeight:600 }}>{nl?'Afspraak':'Appt.'}</span>}
                    {item._src === 'dated' && <span style={{ fontSize:8,color:C.silver,fontStyle:'italic' }}>{dateLabel}</span>}
                  </div>
                  {item.description && <div style={{ fontSize:11,color:C.silver,marginTop:2,lineHeight:1.4 }}>{item.description}</div>}
                  <div style={{ display:'flex',gap:8,marginTop:4 }}>
                    <span style={{ fontSize:12,color:C.purple }}>🧠 {item.mental}%</span>
                    <span style={{ fontSize:12,color:C.teal }}>💪 {item.physical}%</span>
                    <span style={{ fontSize:12,color:C.silver,background:_T.bg4,padding:'1px 7px',borderRadius:20 }}>{item.duration}m</span>
                  </div>
                  {/* Attendees */}
                  {item.attendees?.length > 0 && (
                    <div style={{ display:'flex',flexWrap:'wrap',gap:4,marginTop:6 }}>
                      {item.attendees.map((a, ai) => (
                        <span key={ai} style={{ fontSize:9,padding:'2px 6px',borderRadius:8,fontWeight:600,
                          background: a.status==='accepted'?'#4AC87A20':a.status==='declined'?'#C0505020':'#C8A84B20',
                          color: a.status==='accepted'?'#4AC87A':a.status==='declined'?'#C05050':'#C8A84B',
                          border: `1px solid ${a.status==='accepted'?'#4AC87A44':a.status==='declined'?'#C0505044':'#C8A84B44'}`,
                        }}>
                          {a.channel && <span style={{ marginRight:3 }}>{CHANNELS.find(c=>c.id===a.channel)?.icon||'📩'}</span>}
                          {a.status==='accepted'?'✓':a.status==='declined'?'✗':'⏳'} {nl?'uitgenodigd':'invited'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {item.type === 'custom' && (
                  <button onClick={() => setInviteTarget({ item, dateKey: item._src==='dated'?(item._dateKey||selDayDate):null, dateLabel })}
                    style={{ background:'none',border:'none',color:C.orange,cursor:'pointer',fontSize:15,paddingTop:2,flexShrink:0 }}>
                    📩
                  </button>
                )}
                <button onClick={() => removeItem(item)}
                  style={{ background:'none',border:'none',color:`${C.red}55`,cursor:'pointer',fontSize:14,paddingTop:2,flexShrink:0 }}>
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
      {displayItems.length === 0 && view !== 'all' && (
        <div style={{ ...s_GC({ textAlign:'center',padding:24 }),color:C.silver,fontSize:13 }}>
          {nl?'Nog niets ingepland voor deze dag.':'Nothing scheduled for this day yet.'}
        </div>
      )}

      {/* Invite modal */}
      {inviteTarget && (
        <InviteModal
          item={inviteTarget.item}
          dateLabel={inviteTarget.dateLabel || daysFull[selDay]}
          dateKey={inviteTarget.dateKey || selDayDate}
          lang={lang}
          creatorId={currentUserId || ''}
          creatorName={userName || ''}
          onClose={() => setInviteTarget(null)}
          onAttendeeAdded={(token, channel) => {
            updateItemAttendees(inviteTarget.item, token, channel);
            setInviteTarget(null);
          }}
        />
      )}
    </div>
  );
}

export default WeekScreen;
